import React, { useState, useEffect } from 'react';
import { Users, Copy, LogOut, ArrowRight, Sparkles, MessageSquare } from 'lucide-react';
import { doc, setDoc, updateDoc, deleteDoc, collection, serverTimestamp } from 'firebase/firestore';
import { resilientSnapshot } from '../utils/resilientSnapshot';

const SquadManager = ({ db, appId, user, username, currentSquadId, onClose, onOpenChat, hasUnread }) => {
  const [mode, setMode] = useState('menu'); // 'menu', 'join', 'active'
  const [joinCode, setJoinCode] = useState('');
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (currentSquadId) {
        setMode('active');
        // Roster comes from the squad-scoped members subcollection now.
        const membersRef = collection(db, 'artifacts', appId, 'public', 'data', 'squads', currentSquadId, 'members');
        // resilientSnapshot: on a fresh join this read races the user-doc
        // commit that proves membership — retry instead of showing an empty
        // roster until restart (the "joined DEMO, nothing happened" bug).
        const unsub = resilientSnapshot(membersRef, (snap) => {
            const now = Date.now();
            const list = snap.docs
              .map(d => ({ id: d.id, ...d.data() }))
              .filter(m => !m.lastActive || (now - m.lastActive.toMillis()) < 30 * 60 * 1000);
            setMembers(list);
        }, { label: 'squad-roster' });
        return () => unsub();
    } else {
        setMode('menu');
    }
  }, [currentSquadId, db, appId]);

  // --- ACTIONS ---
  const createSquad = async () => {
    if (!user) return;
    setLoading(true);
    const code = Math.random().toString(36).substring(2, 6).toUpperCase();
    try {
        await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'users', user.uid), {
            squadId: code,
            isSquadHost: true
        }, { merge: true });
        // Join the members subcollection so the roster shows you immediately
        // (location is added later by the sync loop once you have a fix).
        // Write the display name NOW — leaving it to the 15s sync loop made
        // every fresh joiner show as "Anonymous" on the roster/map for a while.
        await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'squads', code, 'members', user.uid), {
            squadId: code, name: username || 'Anonymous', lastActive: serverTimestamp()
        }, { merge: true });
        // Leaving one squad for another: revoke the old membership, or chat/rules
        // access to the previous squad would linger.
        if (currentSquadId && currentSquadId !== code) {
            try { await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'squads', currentSquadId, 'members', user.uid)); } catch (e) { /* best effort */ }
        }
    } catch (e) {
        console.error("Squad Create Error:", e);
        alert("Could not create squad. Check connection.");
    }
    setLoading(false);
  };

  const joinSquad = async (e) => {
    e.preventDefault();
    if (!joinCode.trim() || !user) return;
    setLoading(true);
    try {
        const code = joinCode.trim().toUpperCase();
        await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'users', user.uid), {
            squadId: code,
            isSquadHost: false
        }, { merge: true });
        await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'squads', code, 'members', user.uid), {
            squadId: code, name: username || 'Anonymous', lastActive: serverTimestamp()
        }, { merge: true });
        // Switching squads: revoke the old membership (see createSquad).
        if (currentSquadId && currentSquadId !== code) {
            try { await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'squads', currentSquadId, 'members', user.uid)); } catch (e) { /* best effort */ }
        }
    } catch (e) {
        console.error("Squad Join Error:", e);
        alert("Could not join squad. Check code.");
    }
    setLoading(false);
  };

  const leaveSquad = async () => {
    if (!window.confirm("Leave this squad?") || !user) return;
    try {
        await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'users', user.uid), {
            squadId: null,
            isSquadHost: false
        });
        // Remove our live location from the squad we're leaving.
        if (currentSquadId) {
            try {
                await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'squads', currentSquadId, 'members', user.uid));
            } catch (e) { /* best effort */ }
        }
        setMode('menu');
    } catch (e) { console.error(e); }
  };

  // --- RENDER ---
  return (
    <div className="absolute inset-0 z-[2000] bg-black/95 flex items-center justify-center p-4 animate-in fade-in duration-300">
      <div className="w-full max-w-sm bg-zinc-900 border border-white/10 rounded-[2rem] p-6 shadow-2xl flex flex-col relative overflow-hidden">
        
        {/* Glow FX */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-brand-pink/20 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none"></div>

        {/* Header */}
        <div className="flex justify-between items-center mb-6 relative z-10">
            <div className="flex items-center gap-2">
                <Users className="text-brand-pink" size={24} />
                <h2 className="text-xl font-black italic text-white uppercase tracking-tighter">SQUAD LINK</h2>
            </div>
            <button onClick={onClose} className="text-zinc-500 hover:text-white font-bold text-xs uppercase">Close</button>
        </div>

        {/* --- VIEW: MENU --- */}
        {mode === 'menu' && (
            <div className="flex flex-col gap-4 animate-in slide-in-from-right">
                <button 
                    onClick={createSquad} 
                    disabled={loading}
                    className="bg-brand-pink hover:bg-pink-400 text-white p-6 rounded-2xl flex flex-col items-center gap-2 shadow-lg active:scale-95 transition-all"
                >
                    <Sparkles size={28} className="text-white/80" />
                    <span className="font-black uppercase tracking-widest text-lg">Create Squad</span>
                    <span className="text-[10px] text-white font-bold opacity-80">Start a lobby for your crew</span>
                </button>

                <div className="flex items-center gap-4 text-zinc-600">
                    <div className="h-px bg-zinc-800 flex-1"></div>
                    <span className="text-[10px] font-black uppercase">OR</span>
                    <div className="h-px bg-zinc-800 flex-1"></div>
                </div>

                <button 
                    onClick={() => setMode('join')} 
                    className="bg-zinc-800 hover:bg-zinc-700 text-white p-6 rounded-2xl flex flex-col items-center gap-2 border border-zinc-700 active:scale-95 transition-all"
                >
                    <ArrowRight size={28} className="text-zinc-400" />
                    <span className="font-black uppercase tracking-widest text-lg">Join Squad</span>
                    <span className="text-[10px] text-zinc-400 font-bold opacity-80">Enter a 4-digit code</span>
                </button>
            </div>
        )}

        {/* --- VIEW: JOIN INPUT --- */}
        {mode === 'join' && (
            <form onSubmit={joinSquad} className="flex flex-col gap-4 animate-in slide-in-from-right">
                <div className="bg-zinc-800 p-4 rounded-2xl border border-zinc-700">
                    <label className="text-[9px] font-black text-zinc-500 uppercase mb-2 block">Enter Squad Code</label>
                    <input 
                        autoFocus
                        type="text" 
                        maxLength={4}
                        placeholder="ABCD" 
                        value={joinCode}
                        onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                        className="w-full bg-black border border-zinc-600 rounded-xl p-4 text-center text-3xl font-black text-white tracking-[0.5em] placeholder:text-zinc-800 uppercase focus:outline-none focus:border-brand-teal"
                    />
                </div>
                <div className="flex gap-2">
                    <button type="button" onClick={() => setMode('menu')} className="flex-1 bg-zinc-800 text-zinc-400 font-bold py-4 rounded-xl text-xs uppercase">Back</button>
                    <button type="submit" disabled={joinCode.length < 4 || loading} className="flex-[2] bg-brand-teal text-black font-black py-4 rounded-xl text-xs uppercase shadow-lg disabled:opacity-50">
                        {loading ? 'Joining...' : 'Connect'}
                    </button>
                </div>
            </form>
        )}

        {/* --- VIEW: ACTIVE LOBBY --- */}
        {mode === 'active' && (
            <div className="flex flex-col h-full animate-in slide-in-from-bottom">
                
                {/* Code Display */}
                <div className="bg-zinc-800/50 border border-brand-teal/30 p-4 rounded-2xl mb-4 text-center relative overflow-hidden">
                    <p className="text-[9px] font-black text-brand-teal uppercase mb-1">Your Squad Code</p>
                    <h1 className="text-4xl font-black text-white tracking-[0.2em]">{currentSquadId}</h1>
                    <button onClick={() => { navigator.clipboard.writeText(currentSquadId); }} className="absolute right-3 top-3 text-brand-teal hover:text-white">
                        <Copy size={16} />
                    </button>
                </div>

                {/* Member List */}
                <div className="flex-1 overflow-y-auto min-h-[150px] mb-4 space-y-2 pr-1 no-scrollbar">
                    {members.map(m => (
                        <div key={m.id} className={`flex items-center justify-between p-3 rounded-xl border ${m.id === user.uid ? 'bg-brand-pink/10 border-brand-pink/30' : 'bg-zinc-800/50 border-zinc-700'}`}>
                            <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-black text-xs ${m.id === user.uid ? 'bg-brand-pink text-white' : 'bg-zinc-700 text-zinc-300'}`}>
                                    {m.name ? m.name.charAt(0).toUpperCase() : '?'}
                                </div>
                                <div>
                                    <p className="text-xs font-bold text-white">{m.name || 'Anonymous'}</p>
                                    <p className="text-[9px] font-bold text-zinc-500 uppercase">{m.id === user.uid ? 'You' : (m.location ? 'On Map' : 'Offline')}</p>
                                </div>
                            </div>
                            {m.location && <div className="w-2 h-2 bg-brand-teal rounded-full animate-pulse"></div>}
                        </div>
                    ))}
                </div>

                {/* SQUAD CHAT BUTTON (New) */}
                <button 
                    onClick={onOpenChat}
                    className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-4 rounded-xl flex items-center justify-center gap-2 text-xs uppercase transition-colors mb-2 shadow-lg relative"
                >
                    <MessageSquare size={16} /> Open Squad Chat
                    {hasUnread && (
                        <div className="absolute top-2 right-2 w-3 h-3 bg-red-500 rounded-full border-2 border-indigo-600 animate-pulse"></div>
                    )}
                </button>

                <button onClick={leaveSquad} className="w-full bg-zinc-800 hover:bg-red-900/30 hover:text-red-400 text-zinc-400 font-bold py-4 rounded-xl flex items-center justify-center gap-2 text-xs uppercase transition-colors">
                    <LogOut size={16} /> Leave Squad
                </button>
            </div>
        )}

      </div>
    </div>
  );
};

export default SquadManager;
